import React, { useState } from 'react';
import { MessageSquare, X, Send, Loader2, Star } from 'lucide-react';
import axios from 'axios';
import './FeedbackWidget.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

interface FeedbackWidgetProps {
  userEmail: string;
  isNurseAdmin: boolean;
  selectedDepartment?: string;
  currentPage: 'patient_report' | 'handoffs' | 'clinical_interactions' | 'dashboard' | 'welcome' | 'other';
}

const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
  userEmail,
  isNurseAdmin,
  selectedDepartment,
  currentPage
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      setError('Please enter your feedback');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.post<{ success: boolean }>(`${BACKEND_URL}/feedback/submit`, {
        email: userEmail,
        role: isNurseAdmin ? 'nurse_admin' : 'nurse',
        department: selectedDepartment || '',
        page: currentPage,
        feedback_text: feedback.trim(),
        rating: rating
      });

      if (response.data.success) {
        setSubmitted(true);
        setFeedback('');
        setRating(0);
        // Auto-close after 2 seconds with animation
        setTimeout(() => {
          setIsClosing(true);
          setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
            setSubmitted(false);
          }, 300);
        }, 2000);
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      setFeedback('');
      setRating(0);
      setError('');
      setSubmitted(false);
    }, 300);
  };

  return (
    <div className="feedback-widget-container">
      {isOpen ? (
        <div className={`feedback-panel ${isClosing ? 'closing' : ''}`}>
          <div className="feedback-header">
            <h3>Send Feedback</h3>
            <button className="feedback-close-btn" onClick={handleClose}>
              <X size={18} />
            </button>
          </div>
          
          {submitted ? (
            <div className="feedback-success">
              <div className="success-icon">✓</div>
              <p>Thank you for your feedback!</p>
            </div>
          ) : (
            <>
              <div className="feedback-body">
                <div className="feedback-rating">
                  <span className="rating-label">Rate your experience:</span>
                  <div className="star-rating">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star-btn ${star <= (hoverRating || rating) ? 'active' : ''}`}
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                      >
                        <Star
                          size={24}
                          fill={star <= (hoverRating || rating) ? '#fbbf24' : 'none'}
                          stroke={star <= (hoverRating || rating) ? '#fbbf24' : '#94a3b8'}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  className="feedback-textarea"
                  placeholder="Share your thoughts, suggestions, or report any issues..."
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  maxLength={1000}
                  disabled={isSubmitting}
                />
                <div className="feedback-char-count">
                  {feedback.length}/1000
                </div>
                {error && <div className="feedback-error">{error}</div>}
              </div>
              
              <div className="feedback-footer">
                <button 
                  className="feedback-submit-btn"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !feedback.trim()}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="spinning" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      <span>Submit</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <button 
          className="feedback-trigger-btn"
          onClick={() => setIsOpen(true)}
          title="Send Feedback"
        >
          <MessageSquare size={24} />
        </button>
      )}
    </div>
  );
};

export default FeedbackWidget;
